import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle, ChevronRight, Activity, RefreshCw, Flame, Zap,
  FlaskConical, FileCheck2, Users, LifeBuoy, Sparkles, HeartPulse,
  Layers, Brain, Target, TrendingUp, Clock, CheckCircle2, Shield,
  ArrowUpRight, Workflow, Inbox, Beaker, Dna, Gauge, Hourglass,
  Play, ClipboardList,
} from 'lucide-react';
import CrossSystemSignals from '@/components/terra/CrossSystemSignals';

// ── Module registry (the 8 cockpit entry-points) ──────────────
const CORE_MODULES = [
  { path: '/admin/crm',                 label: 'CRM Pipeline',      icon: Inbox,        accent: '#D4A843' },
  { path: '/admin/lab-intelligence',    label: 'Lab Intelligence',  icon: FlaskConical, accent: '#7E9EC8' },
  { path: '/admin/biological-context',  label: 'Biological Ctx',    icon: Beaker,       accent: '#C89B7B' },
  { path: '/admin/biocontext-memory',    label: 'Bio Context 2.0',   icon: Dna,          accent: '#a78bfa' },
  { path: '/admin/decode-wizard',       label: 'DECODE Workspace',  icon: Sparkles,     accent: '#10b981' },
  { path: '/admin/care-ops',            label: 'Care Operations',   icon: HeartPulse,   accent: '#f43f5e' },
  { path: '/admin/support-os',          label: 'Support',           icon: LifeBuoy,     accent: '#f59e0b' },
  { path: '/admin/mindflo-review',      label: 'MindFlō / TwinFlō', icon: Brain,        accent: '#06b6d4' },
];

// ── CRM stage order + labels ───────────────────────────────────
const CRM_STAGE_ORDER = [
  'NEW', 'CONTACTED', 'EVALUATION_READY', 'CLIENT_ACCOUNT_CREATED',
  'INTAKE_RECEIVED', 'PREDECODE_READY', 'LABS_RECOMMENDED', 'LABS_RECEIVED',
  'DECODE_READY', 'DECISION_PENDING', 'PAYMENT_CONFIRMED', 'SUBSCRIBER_CONVERTED',
  'ON_HOLD', 'LOST',
];
const CRM_STAGE_LABEL = {
  NEW: 'New', CONTACTED: 'Contacted', EVALUATION_READY: 'Eval Ready',
  CLIENT_ACCOUNT_CREATED: 'Account', INTAKE_RECEIVED: 'Intake',
  PREDECODE_READY: 'PreDECODE', LABS_RECOMMENDED: 'Labs Rec',
  LABS_RECEIVED: 'Labs In', DECODE_READY: 'DECODE Ready',
  DECISION_PENDING: 'Decision', PAYMENT_CONFIRMED: 'Paid',
  SUBSCRIBER_CONVERTED: 'Live', ON_HOLD: 'Hold', LOST: 'Lost',
};
const CRM_STAGE_COLOR = {
  NEW: '#D4A843', CONTACTED: '#D4A843', EVALUATION_READY: '#7E9EC8',
  CLIENT_ACCOUNT_CREATED: '#7E9EC8', INTAKE_RECEIVED: '#06b6d4',
  PREDECODE_READY: '#06b6d4', LABS_RECOMMENDED: '#a78bfa',
  LABS_RECEIVED: '#a78bfa', DECODE_READY: '#10b981',
  DECISION_PENDING: '#10b981', PAYMENT_CONFIRMED: '#10b981',
  SUBSCRIBER_CONVERTED: '#10b981', ON_HOLD: '#64748b', LOST: '#475569',
};

// ── Journey modes ──────────────────────────────────────────────
const JOURNEY_ORDER = ['PREDECODE', 'DECODE_PENDING', 'DECODE_COMPLETE',
                       'THERAPEUTIC_CORRECTION', 'BASELINE_ACHIEVED', 'PERFORMATIVE'];
const JOURNEY_LABEL = {
  PREDECODE: 'Pre-DECODE', DECODE_PENDING: 'DECODE Pending', DECODE_COMPLETE: 'DECODE Done',
  THERAPEUTIC_CORRECTION: 'Therapeutic', BASELINE_ACHIEVED: 'Baseline', PERFORMATIVE: 'Performative',
};
const JOURNEY_COLOR = {
  PREDECODE: '#64748b', DECODE_PENDING: '#3b82f6', DECODE_COMPLETE: '#06b6d4',
  THERAPEUTIC_CORRECTION: '#f43f5e', BASELINE_ACHIEVED: '#f59e0b', PERFORMATIVE: '#10b981',
};

// ── Activity icons ─────────────────────────────────────────────
const ACTIVITY_ICON = {
  lab_validated: FlaskConical,
  decode_published: Sparkles,
  support_escalated: AlertTriangle,
  subscriber_converted: CheckCircle2,
};
const ACTIVITY_COLOR = {
  lab_validated: '#7E9EC8',
  decode_published: '#10b981',
  support_escalated: '#f43f5e',
  subscriber_converted: '#D4A843',
};

// ── KPI Tile ────────────────────────────────────────────────────
function KpiTile({ label, value, icon: Icon, accent, onClick, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className="group rounded-xl p-3.5 border border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12] hover:bg-white/[0.03] transition-all cursor-pointer min-w-0"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}14` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
        <p className="text-[9.5px] uppercase tracking-[0.14em] text-white/35 font-['Work_Sans'] leading-tight truncate">{label}</p>
      </div>
      <p className="text-2xl font-['Outfit'] font-light leading-none" style={{ color: accent }}>
        {value ?? '—'}
      </p>
    </div>
  );
}

// ── Action Queue Row ────────────────────────────────────────────
function ActionRow({ item, onGo }) {
  const sev = item.severity;
  const accent = sev === 'critical' ? '#f43f5e' : sev === 'high' ? '#f59e0b' : '#7E9EC8';
  const bg = sev === 'critical' ? 'bg-red-500/[0.06]' : sev === 'high' ? 'bg-amber-500/[0.05]' : 'bg-white/[0.02]';
  const border = sev === 'critical' ? 'border-red-500/20' : sev === 'high' ? 'border-amber-500/15' : 'border-white/[0.06]';
  return (
    <div
      data-testid={`action-row-${item.key}`}
      className={`flex items-center gap-3 p-3 rounded-lg border ${border} ${bg} hover:border-white/20 transition-colors group cursor-pointer`}
      onClick={() => onGo(item.link)}
    >
      <div className="w-1.5 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-white/80 font-['Work_Sans'] leading-tight">{item.label}</p>
        <p className="text-[10px] text-white/35 mt-0.5 uppercase tracking-wider">{sev}</p>
      </div>
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <span className="text-xl font-['Outfit'] font-light" style={{ color: accent }}>{item.count}</span>
        <ArrowUpRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white/60 transition-colors" />
      </div>
    </div>
  );
}

// ── Pipeline band (stage distribution) ──────────────────────────
function PipelineBand({ title, data, order, labelMap, colorMap, testid }) {
  const total = Object.values(data || {}).reduce((s, v) => s + v, 0);
  return (
    <div data-testid={testid} className="rounded-xl p-5 border border-white/[0.06]" style={{ backgroundColor: '#161B22' }}>
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-['Work_Sans']">{title}</p>
        <span className="text-[10px] text-white/40 font-mono">{total} total</span>
      </div>
      {/* Stacked band */}
      {total > 0 && (
        <div className="flex h-2 w-full rounded-full overflow-hidden bg-white/[0.04] mb-3">
          {order.map(k => {
            const c = data?.[k] || 0;
            if (!c) return null;
            return <div key={k} style={{ width: `${(c/total)*100}%`, backgroundColor: colorMap[k] }} title={`${labelMap[k]}: ${c}`} />;
          })}
        </div>
      )}
      {/* Stages list */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {order.map(k => {
          const c = data?.[k] || 0;
          if (!c && !['NEW','PREDECODE','SUBSCRIBER_CONVERTED','DECODE_COMPLETE'].includes(k)) return null;
          return (
            <div key={k} className="flex items-center gap-2 text-[11px] font-['Work_Sans']">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[k] }} />
              <span className="text-white/50 flex-1 truncate">{labelMap[k]}</span>
              <span className="font-mono text-white/70">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Intelligence tile (a signal axis) ───────────────────────────
function IntelligenceTile({ label, count, icon: Icon, accent, top = [], onClick, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className="rounded-xl p-4 border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15] transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: accent }} />
          <p className="text-[11px] text-white/60 font-['Work_Sans'] leading-tight">{label}</p>
        </div>
        <span className="text-xl font-['Outfit'] font-light" style={{ color: accent }}>{count}</span>
      </div>
      {top.length > 0 ? (
        <div className="space-y-0.5 mt-2">
          {top.slice(0, 3).map((s, i) => (
            <p key={i} className="text-[10px] text-white/40 font-['Work_Sans'] truncate">
              • {s.subscriber_name}
            </p>
          ))}
          {top.length > 3 && (
            <p className="text-[9.5px] text-white/25 font-['Work_Sans'] italic">+{top.length - 3} more</p>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-white/25 italic font-['Work_Sans'] mt-1">None flagged</p>
      )}
    </div>
  );
}

// ── Recent Activity Feed ────────────────────────────────────────
function ActivityFeed({ items }) {
  const navigate = useNavigate();
  if (!items || items.length === 0) {
    return <p className="text-xs text-white/30 italic font-['Work_Sans']">No recent movement.</p>;
  }
  return (
    <div className="space-y-1.5" data-testid="activity-feed">
      {items.map((it, i) => {
        const Icon = ACTIVITY_ICON[it.type] || Activity;
        const accent = ACTIVITY_COLOR[it.type] || '#64748b';
        const ts = it.at ? new Date(it.at) : null;
        const timeLabel = ts ? ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        return (
          <div
            key={i}
            className="flex items-center gap-2.5 py-1.5 px-2 rounded hover:bg-white/[0.03] cursor-pointer transition-colors"
            onClick={() => it.link && navigate(it.link)}
          >
            <Icon className="w-3 h-3 flex-shrink-0" style={{ color: accent }} />
            <span className="text-[11px] text-white/35 font-mono w-12 flex-shrink-0">{timeLabel}</span>
            <span className="text-[11px] text-white/55 font-['Work_Sans'] flex-shrink-0 max-w-[110px] truncate">
              {it.subscriber_name}
            </span>
            <span className="text-[11px] text-white/70 font-['Work_Sans'] truncate flex-1">{it.message}</span>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//                          MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // 'stale' | 'drafts' | null
  const [runningIntel, setRunningIntel] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/care-os-cockpit`);
      setData(res.data);
    } catch (e) { console.error('Cockpit load error:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (['admin', 'counselor', 'health_coach', 'doctor'].includes(user?.role)) load();
    else setLoading(false);
  }, [user]);

  const rerunIntelligence = async (subId, subName) => {
    setRunningIntel(p => ({ ...p, [subId]: true }));
    try {
      await axios.post(`${API}/regenflo-intelligence/lab-intelligence/${subId}`);
      toast.success(`Intelligence re-run triggered for ${subName}`);
      setTimeout(load, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-run failed');
    } finally {
      setRunningIntel(p => ({ ...p, [subId]: false }));
    }
  };

  const snapshot = data?.today_snapshot || {};
  const intel = data?.intelligence_snapshot || {};

  const kpis = useMemo(() => ([
    { key: 'inquiries', label: 'New Inquiries', value: snapshot.new_inquiries, icon: Inbox, accent: '#D4A843',
      action: () => navigate('/admin/crm?stage=NEW'), testid: 'kpi-inquiries' },
    { key: 'evaluation', label: 'Evaluation Cases', value: snapshot.evaluation_cases, icon: Workflow, accent: '#7E9EC8',
      action: () => navigate('/admin/crm?bucket=evaluation'), testid: 'kpi-evaluation' },
    { key: 'labs', label: 'Pending Lab Validation', value: snapshot.pending_lab_validations, icon: FileCheck2, accent: '#a78bfa',
      action: () => navigate('/admin/lab-intelligence?tab=validation'), testid: 'kpi-lab-validations' },
    { key: 'drafts', label: 'DECODE Drafts Pending', value: snapshot.pending_decode_drafts, icon: Sparkles, accent: '#10b981',
      action: () => setDrawer('drafts'), testid: 'kpi-decode-drafts' },
    { key: 'active', label: 'Active Subscribers', value: snapshot.active_subscribers, icon: Users, accent: '#06b6d4',
      action: () => navigate('/admin/users'), testid: 'kpi-active-subs' },
    { key: 'support', label: 'Support Escalations', value: snapshot.support_escalations, icon: LifeBuoy, accent: '#f43f5e',
      action: () => navigate('/admin/support-os?filter=escalated'), testid: 'kpi-support' },
    { key: 'stale', label: 'Stale Intelligence', value: snapshot.stale_intelligence_cases, icon: Hourglass, accent: '#f59e0b',
      action: () => setDrawer('stale'), testid: 'kpi-stale-intel' },
    { key: 'biocontext_reviews', label: 'Biological Context Reviews Pending',
      value: snapshot.biocontext_pending_reviews, icon: ClipboardList, accent: '#c084fc',
      action: () => navigate('/admin/biocontext-memory?tab=input-queue&filter=pending'),
      testid: 'kpi-biocontext-reviews' },
  ]), [snapshot, navigate]);

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.28em] text-white/30 mb-1.5 font-['Work_Sans']">
              ReGenflō Care OS · Operating Cockpit
            </p>
            <h1 data-testid="admin-dashboard-title" className="text-2xl sm:text-3xl font-['Outfit'] font-light text-white/90">
              Welcome, {user?.name?.split(' ')[0] || 'Steward'}.
            </h1>
            <p className="text-xs text-white/40 font-['Work_Sans'] mt-1">
              Live state across CRM, Labs, Intelligence, DECODE, and Care Operations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/care-command-center">
              <Button variant="outline" size="sm" className="h-8 px-3 border-white/10 text-white/60 hover:text-white/90 text-[10.5px]">
                <Shield className="w-3 h-3 mr-1.5" /> Decision Center
              </Button>
            </Link>
            <Button variant="ghost" size="sm" className="text-white/30 hover:text-white/60" onClick={load} data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-[10px]">Refresh</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="w-5 h-5 text-white/20 animate-spin mx-auto" />
            <p className="text-xs text-white/20 mt-2 font-['Work_Sans']">Loading operating state…</p>
          </div>
        ) : data ? (
          <>
            {/* ── 0. Cross-System Signals — calm awareness layer ── */}
            <CrossSystemSignals />

            {/* ── 1. Today's operational overview (KPI row) ── */}
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <Gauge className="w-3.5 h-3.5 text-white/40" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-['Work_Sans']">Today's Operational Snapshot</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5" data-testid="kpi-row">
                {kpis.map(k => (
                  <KpiTile key={k.key} label={k.label} value={k.value} icon={k.icon} accent={k.accent} testid={k.testid}
                    onClick={k.action} />
                ))}
              </div>
            </div>

            {/* ── 2. High-priority action queue ── */}
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <Flame className="w-3.5 h-3.5 text-red-400/80" />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-red-400/70 font-['Work_Sans']">
                    What Needs Action Now ({data.action_queue?.length || 0})
                  </p>
                </div>
              </div>
              {(data.action_queue?.length || 0) === 0 ? (
                <div className="rounded-xl p-5 border border-emerald-900/20 bg-emerald-500/[0.03] flex items-center gap-3" data-testid="action-queue-clear">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400/80" />
                  <p className="text-xs text-emerald-400/80 font-['Work_Sans']">All clear. No workflow items demand action right now.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid="action-queue">
                  {data.action_queue.map(item => (
                    <ActionRow key={item.key} item={item} onGo={(l) => navigate(l)} />
                  ))}
                </div>
              )}
            </div>

            {/* ── 3. Pipeline: CRM + Journey ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PipelineBand
                testid="crm-pipeline"
                title="CRM Pipeline — Lead → Subscriber"
                data={data.pipeline?.crm_by_stage || {}}
                order={CRM_STAGE_ORDER}
                labelMap={CRM_STAGE_LABEL}
                colorMap={CRM_STAGE_COLOR}
              />
              <PipelineBand
                testid="journey-distribution"
                title="Subscriber Journey Modes"
                data={data.pipeline?.journey_by_mode || {}}
                order={JOURNEY_ORDER}
                labelMap={JOURNEY_LABEL}
                colorMap={JOURNEY_COLOR}
              />
            </div>

            {/* ── 4. Intelligence snapshot ── */}
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <Brain className="w-3.5 h-3.5 text-[#a78bfa]" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-['Work_Sans']">Intelligence Snapshot</p>
                <span className="text-[10px] text-white/25 font-['Work_Sans']">across active subscribers</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2.5" data-testid="intelligence-snapshot">
                <IntelligenceTile testid="intel-lab-concern" label="Lab-driven concern" count={intel.strong_lab_concern?.count || 0}
                  top={intel.strong_lab_concern?.top || []} icon={FlaskConical} accent="#7E9EC8"
                  onClick={() => navigate('/admin/lab-intelligence')} />
                <IntelligenceTile testid="intel-twinflo" label="TwinFlō influence" count={intel.twinflo_influence?.count || 0}
                  top={intel.twinflo_influence?.top || []} icon={Target} accent="#06b6d4"
                  onClick={() => navigate('/admin/mindflo-review')} />
                <IntelligenceTile testid="intel-execution" label="Execution blocker" count={intel.execution_blocker?.count || 0}
                  top={intel.execution_blocker?.top || []} icon={Zap} accent="#f59e0b"
                  onClick={() => navigate('/admin/execution-intelligence')} />
                <IntelligenceTile testid="intel-passive" label="Passive strain" count={intel.passive_strain?.count || 0}
                  top={intel.passive_strain?.top || []} icon={Activity} accent="#a78bfa"
                  onClick={() => navigate('/admin/execution-intelligence')} />
                <IntelligenceTile testid="intel-unstable" label="Unstable today" count={intel.unstable_today?.count || 0}
                  top={intel.unstable_today?.top || []} icon={AlertTriangle} accent="#f43f5e"
                  onClick={() => navigate('/admin/care-command-center')} />
                <IntelligenceTile testid="intel-stale" label="Stale / no intel" count={intel.stale_or_no_intel?.count || 0}
                  top={intel.stale_or_no_intel?.top || []} icon={Hourglass} accent="#64748b"
                  onClick={() => navigate('/admin/decode-wizard')} />
              </div>
            </div>

            {/* ── 5. Recent activity + Workflow queues ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Recent activity */}
              <div data-testid="recent-activity" className="rounded-xl p-5 border border-white/[0.06] lg:col-span-2" style={{ backgroundColor: '#161B22' }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 text-white/40" />
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-['Work_Sans']">System Movement — Last 14d</p>
                  </div>
                </div>
                <ActivityFeed items={data.recent_activity || []} />
              </div>

              {/* Workflow bundles (drafts-to-publish, labs-ready) */}
              <div className="space-y-3">
                <div className="rounded-xl p-4 border border-white/[0.06]" style={{ backgroundColor: '#161B22' }} data-testid="drafts-ready">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-400/80" />
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-['Work_Sans']">DECODE Drafts</p>
                    </div>
                    <Link to="/admin/decode-wizard" className="text-[10px] text-white/40 hover:text-white/70 flex items-center gap-0.5">
                      Open <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                  {(data.drafts_ready_to_publish || []).length === 0 ? (
                    <p className="text-[11px] text-white/30 italic font-['Work_Sans']">No drafts awaiting publish.</p>
                  ) : (
                    <div className="space-y-1">
                      {data.drafts_ready_to_publish.slice(0, 5).map((d, i) => (
                        <div key={i} className="flex items-center gap-2 py-1">
                          <span className="w-1 h-1 rounded-full bg-emerald-400/70" />
                          <span className="text-[11px] text-white/65 font-['Work_Sans'] truncate flex-1">{d.subscriber_name}</span>
                          <span className="text-[9.5px] text-white/30 font-mono">{d.updated_at ? new Date(d.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-xl p-4 border border-white/[0.06]" style={{ backgroundColor: '#161B22' }} data-testid="labs-ready">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="w-3.5 h-3.5 text-[#7E9EC8]" />
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-['Work_Sans']">Labs Ready for Intel</p>
                    </div>
                    <Link to="/admin/lab-intelligence" className="text-[10px] text-white/40 hover:text-white/70 flex items-center gap-0.5">
                      Open <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                  {(data.labs_ready_for_intel || []).length === 0 ? (
                    <p className="text-[11px] text-white/30 italic font-['Work_Sans']">No new validated labs waiting on intelligence.</p>
                  ) : (
                    <div className="space-y-1">
                      {data.labs_ready_for_intel.slice(0, 5).map((r, i) => (
                        <div key={i} className="flex items-center gap-2 py-1">
                          <span className="w-1 h-1 rounded-full" style={{ backgroundColor: '#7E9EC8' }} />
                          <span className="text-[11px] text-white/65 font-['Work_Sans'] truncate flex-1">{r.subscriber_name}</span>
                          <span className="text-[9.5px] text-white/30 font-mono">{r.validated_at ? new Date(r.validated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── 6. Core modules ── */}
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <Layers className="w-3.5 h-3.5 text-white/40" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-['Work_Sans']">Core Modules</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="core-modules">
                {CORE_MODULES.map(m => (
                  <Link key={m.path} to={m.path} data-testid={`module-${m.path.replace(/\//g, '-')}`}>
                    <div className="group rounded-lg p-3 border border-white/[0.06] bg-white/[0.015] hover:border-white/20 hover:bg-white/[0.04] transition-all h-full flex flex-col items-center text-center gap-1.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${m.accent}14` }}>
                        <m.icon className="w-4 h-4" style={{ color: m.accent }} />
                      </div>
                      <span className="text-[10.5px] text-white/55 group-hover:text-white/85 font-['Work_Sans'] leading-tight">
                        {m.label}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Footer meta */}
            <div className="flex items-center gap-2 pt-2 text-[10px] text-white/20 font-['Work_Sans']">
              <Clock className="w-3 h-3" />
              <span>Last refreshed {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}</span>
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <p className="text-xs text-white/30 font-['Work_Sans']">Unable to load cockpit. Try refreshing.</p>
          </div>
        )}
      </div>

      {/* ── Actionable drawers ── */}
      <Dialog open={drawer === 'stale'} onOpenChange={(o) => !o && setDrawer(null)}>
        <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-2xl" data-testid="stale-intel-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-['Outfit'] font-light">
              <Hourglass className="w-4 h-4 text-[#f59e0b]" />
              Stale or Missing Intelligence
              <span className="text-[10px] text-white/30 ml-2">{intel.stale_or_no_intel?.count || 0} subscribers</span>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            {(intel.stale_or_no_intel?.top || []).length === 0 ? (
              <p className="text-xs text-white/35 italic py-6 text-center">No stale cases.</p>
            ) : (
              <div className="space-y-1.5">
                {(intel.stale_or_no_intel.top).map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]" data-testid={`stale-row-${s.subscriber_id}`}>
                    <div className="w-1 h-8 rounded-full bg-amber-500/50 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white/80 font-['Work_Sans'] truncate">{s.subscriber_name}</p>
                      <p className="text-[10px] text-white/35 mt-0.5">
                        {s.reason === 'no_intelligence_run' ? 'No intelligence run yet' :
                         s.reason === 'stale_run' ? `Last run: ${s.last_run_at ? new Date(s.last_run_at).toLocaleDateString() : '—'}` : s.reason}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!!runningIntel[s.subscriber_id]}
                      data-testid={`rerun-intel-${s.subscriber_id}`}
                      className="h-7 px-2.5 text-[10px] border-amber-700/30 text-amber-400 hover:bg-amber-900/20"
                      onClick={() => rerunIntelligence(s.subscriber_id, s.subscriber_name)}
                    >
                      {runningIntel[s.subscriber_id] ? (
                        <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Running</>
                      ) : (
                        <><Play className="w-3 h-3 mr-1" /> Re-run</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[10px] text-white/35 hover:text-white/70"
                      onClick={() => navigate(`/admin/lab-intelligence?subscriber=${s.subscriber_id}&tab=intelligence`)}
                    >
                      Open <ChevronRight className="w-3 h-3 ml-0.5" />
                    </Button>
                  </div>
                ))}
                {(intel.stale_or_no_intel.count || 0) > (intel.stale_or_no_intel.top || []).length && (
                  <p className="text-[10px] text-white/30 italic pt-2 text-center font-['Work_Sans']">
                    + {(intel.stale_or_no_intel.count) - (intel.stale_or_no_intel.top || []).length} more subscribers with stale intelligence
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={drawer === 'drafts'} onOpenChange={(o) => !o && setDrawer(null)}>
        <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-2xl" data-testid="decode-drafts-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-['Outfit'] font-light">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              DECODE Drafts Pending Publish
              <span className="text-[10px] text-white/30 ml-2">{(data?.drafts_ready_to_publish || []).length} drafts</span>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            {(data?.drafts_ready_to_publish || []).length === 0 ? (
              <p className="text-xs text-white/35 italic py-6 text-center">No drafts awaiting publish.</p>
            ) : (
              <div className="space-y-1.5">
                {(data.drafts_ready_to_publish).map((d, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]" data-testid={`draft-row-${d.subscriber_id}`}>
                    <div className="w-1 h-8 rounded-full bg-emerald-500/50 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white/80 font-['Work_Sans'] truncate">{d.subscriber_name}</p>
                      <p className="text-[10px] text-white/35 mt-0.5">
                        Last edited {d.updated_at ? new Date(d.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`open-draft-${d.subscriber_id}`}
                      className="h-7 px-2.5 text-[10px] border-emerald-700/30 text-emerald-400 hover:bg-emerald-900/20"
                      onClick={() => navigate(`/admin/decode-wizard?subscriber=${d.subscriber_id}`)}
                    >
                      Open DECODE <ChevronRight className="w-3 h-3 ml-0.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
